import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiUtils } from '../utils/apiUtils';
import { checkPatientAuth } from '../utils/patientAuth';

const MessageUnreadContext = createContext({
  unreadCount: 0,
  lastChecked: null,
  refreshUnreadCount: () => {},
});

export function useMessageUnread() {
  const context = useContext(MessageUnreadContext);
  if (!context) {
    throw new Error('useMessageUnread must be used within MessageUnreadProvider');
  }
  return context;
}

export function MessageUnreadProvider({ children }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastChecked, setLastChecked] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication on mount
  useEffect(() => {
    const verifyAuth = async () => {
      const { authenticated } = await checkPatientAuth();
      setIsAuthenticated(authenticated);
    };
    verifyAuth();
  }, []);

  // Check unread messages
  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiUtils.get('/api/messages/unread-count');
      if (response.success) {
        setUnreadCount(response.total || 0);
        setLastChecked(new Date());
        return response.total || 0;
      }
    } catch (error) {
      console.error('[MessageUnread] Error checking unread messages:', error);
      // Don't set to 0 on error - keep last known count
    }
    return unreadCount;
  }, [isAuthenticated, unreadCount]);

  // Fetch once when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      refreshUnreadCount();
    }
  }, [isAuthenticated, refreshUnreadCount]);

  const value = {
    unreadCount,
    lastChecked,
    refreshUnreadCount,
  };

  return (
    <MessageUnreadContext.Provider value={value}>
      {children}
    </MessageUnreadContext.Provider>
  );
}
